import { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { X, Loader2, Plus, Layers, ShieldAlert, Link2, Tag, Clock, Users } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useUIStore } from '@/client/stores/ui-store';
import { useAuth } from '@/client/hooks/use-auth';
import { createLinkSchema, type CreateLinkInput } from '@/client/lib/validations';
import { apiFetch } from '@/client/lib/api';
import { cn } from '@/client/lib/utils';
import { useKeyboard } from '@/client/hooks/use-keyboard';
import { useIsMobile } from '@/client/hooks/use-mobile';
import { useFeatureGate } from '@/client/components/feature-gate';

interface ProjectOption {
  id: string;
  name: string;
  color: string;
}

// Armenian cultural placeholder examples
// These phrases celebrate Armenian identity and add warmth to the UX
// Each entry has a matching URL slug and title explanation
// See vision/culture.md section 4.2 for full vocabulary
const ARMENIAN_PLACEHOLDERS = [
  {
    url: 'https://example.com/tsavt-tanem',
    title: 'Tsavt Tanem — Let me take your pain',
  },
  {
    url: 'https://example.com/jigyar',
    title: 'Jigyar — My liver (term of endearment)',
  },
  {
    url: 'https://example.com/hajogh-jan',
    title: 'Hajogh — Good luck!',
  },
  {
    url: 'https://example.com/karot',
    title: 'Karot — Longing, yearning for homeland',
  },
  {
    url: 'https://example.com/achkt-luys',
    title: "Achk't Luys — Light to your eye!",
  },
  {
    url: 'https://example.com/mernem-janid',
    title: 'Mernem Janid — I would die for you',
  },
  {
    url: 'https://example.com/hamov',
    title: 'Hamov — Delightful, charming',
  },
  {
    url: 'https://example.com/glukhs-mi-hardukir',
    title: "Glukhs Mi Hardukir — Don't stress!",
  },
  {
    url: 'https://example.com/your-long-url',
    title: 'My link title',
  },
] as const;

/**
 * VanityUrlField - Custom short code input with feature gating
 *
 * Uses TSK-020 "always show" pattern: the input is always visible,
 * but non-Plus users see a gated version that opens the upgrade modal on click.
 */
interface VanityUrlFieldProps {
  register: ReturnType<typeof useForm<CreateLinkInput>>['register'];
  error?: string;
}

function VanityUrlField({ register, error }: VanityUrlFieldProps) {
  const { hasAccess, openUpgradeModal, UpgradeModalElement } = useFeatureGate(
    'custom_short_codes',
    'Create branded vanity URLs like arml.ink/your-brand'
  );

  return (
    <div className="space-y-2">
      <label htmlFor="customCode" className="flex items-center gap-2 text-sm font-medium">
        Vanity URL
        <span className="text-muted-foreground text-xs">(optional)</span>
      </label>

      {hasAccess ? (
        // Plus user: show editable input
        <div className="flex items-center">
          <span className="px-3 py-2.5 rounded-l-xl border border-r-0 bg-muted text-muted-foreground text-sm">
            arml.ink/
          </span>
          <input
            type="text"
            id="customCode"
            autoComplete="off"
            spellCheck={false}
            {...register('customCode')}
            className="flex-1 px-3 py-2.5 rounded-r-xl border bg-background focus:outline-none focus:ring-2 focus:ring-primary font-mono text-sm"
            placeholder="your-brand-name"
          />
        </div>
      ) : (
        // Free user: show gated input that opens upgrade modal
        <div
          onClick={openUpgradeModal}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              openUpgradeModal();
            }
          }}
          role="button"
          tabIndex={0}
          aria-label="Upgrade to Plus to unlock vanity URLs"
          className="relative cursor-pointer group"
        >
          <div className="flex items-center opacity-50 pointer-events-none">
            <span className="px-3 py-2.5 rounded-l-xl border border-r-0 bg-muted text-muted-foreground text-sm">
              arml.ink/
            </span>
            <div className="flex-1 px-3 py-2.5 rounded-r-xl border bg-background font-mono text-sm text-muted-foreground">
              your-brand-name
            </div>
          </div>
          {/* Upgrade badge */}
          <span className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-amber-500/10 text-amber-600 dark:text-amber-400">
            <svg className="size-2.5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M2.5 19h19l-2-7-6.5 2.5L12 8l-1 6.5L4.5 12l-2 7z" />
            </svg>
            Plus
          </span>
        </div>
      )}

      {error && <p className="text-sm text-destructive">{error}</p>}
      {UpgradeModalElement}
    </div>
  );
}

export function CreateLinkModal() {
  const { createLinkModalOpen, closeCreateLinkModal } = useUIStore();
  const { isPlusUser } = useAuth();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [tagInput, setTagInput] = useState('');
  const queryClient = useQueryClient();

  // Pick random placeholder pair that persists during the modal session
  const placeholder = useMemo(() => {
    const index = Math.floor(Math.random() * ARMENIAN_PLACEHOLDERS.length);
    return ARMENIAN_PLACEHOLDERS[index] ?? ARMENIAN_PLACEHOLDERS[0];
  }, [createLinkModalOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch projects for Plus users
  const { data: projectsData } = useQuery({
    queryKey: ['projects'],
    queryFn: async () => {
      const response = await apiFetch('/api/projects');
      if (!response.ok) return { data: [] };
      return response.json();
    },
    enabled: isPlusUser && createLinkModalOpen,
  });

  const projects: ProjectOption[] = projectsData?.data || [];

  const form = useForm<CreateLinkInput>({
    resolver: zodResolver(createLinkSchema),
    defaultValues: {
      url: '',
      title: '',
      tags: [],
      validUntil: null,
      maxVisits: null,
      customCode: undefined,
      projectId: undefined,
    },
  });

  const [unsafeUrlError, setUnsafeUrlError] = useState<string | null>(null);

  const createLinkMutation = useMutation({
    mutationFn: async (data: CreateLinkInput) => {
      setUnsafeUrlError(null);
      const response = await apiFetch('/api/links', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const error = await response.json();
        if (error.code === 'UNSAFE_URL') {
          const unsafeError = new Error(error.message) as Error & {
            isUnsafe: boolean;
            threatType: string;
          };
          unsafeError.isUnsafe = true;
          unsafeError.threatType = error.threatType;
          throw unsafeError;
        }
        throw new Error(error.message || 'Failed to create link');
      }
      return response.json();
    },
    onSuccess: (data) => {
      toast.success('Link created successfully!', {
        description: `arml.ink/${data.shortCode}`,
        action: {
          label: 'Copy',
          onClick: () => {
            navigator.clipboard.writeText(data.shortUrl);
            toast.success('Link copied!');
          },
        },
      });
      queryClient.invalidateQueries({ queryKey: ['links'] });
      closeCreateLinkModal();
      form.reset();
      // Navigate to the link details page to show analytics
      navigate(`/links/${data.shortCode}`);
    },
    onError: (error: Error & { isUnsafe?: boolean }) => {
      if (error.isUnsafe) {
        setUnsafeUrlError(error.message);
      } else {
        toast.error(error.message || 'Failed to create link');
      }
    },
  });

  const onSubmit = (data: CreateLinkInput) => {
    // Clean up form data before sending to API
    const cleanedData = {
      ...data,
      // Filter out empty customCode to allow auto-generation
      customCode: data.customCode?.trim() || undefined,
      // Convert empty string to undefined for validUntil (datetime-local returns "" when empty)
      validUntil: data.validUntil || undefined,
      // Convert NaN to undefined for maxVisits (number input returns NaN when empty)
      maxVisits: Number.isNaN(data.maxVisits) ? undefined : data.maxVisits || undefined,
      // Convert empty string to undefined for projectId
      projectId: data.projectId || undefined,
    };
    createLinkMutation.mutate(cleanedData);
  };

  const addTag = () => {
    const tag = tagInput.trim().toLowerCase();
    if (tag && !form.getValues('tags')?.includes(tag)) {
      form.setValue('tags', [...(form.getValues('tags') || []), tag]);
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    form.setValue('tags', form.getValues('tags')?.filter((t) => t !== tagToRemove) || []);
  };

  // Handle modal open/close: reset form on close, check for pre-filled URL on open
  useEffect(() => {
    if (createLinkModalOpen) {
      // Check for pre-filled URL from QuickCreate component
      const prefillUrl = sessionStorage.getItem('quickCreateUrl');
      if (prefillUrl) {
        form.setValue('url', prefillUrl);
        sessionStorage.removeItem('quickCreateUrl');
      }
    } else {
      // Reset form when modal closes
      form.reset();
      setTagInput('');
      setUnsafeUrlError(null);
    }
  }, [createLinkModalOpen, form]);

  // ⚡ Performance Optimization: Use shared keyboard hook instead of manual listener
  useKeyboard('Escape', closeCreateLinkModal, createLinkModalOpen);

  if (!createLinkModalOpen) return null;

  const tags = form.watch('tags') || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={closeCreateLinkModal}
      />

      {/* Modal */}
      <div className="relative z-10 w-full max-w-xl bg-background rounded-2xl shadow-xl border animate-in fade-in zoom-in-95 duration-200 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-primary/5 to-transparent">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Link2 className="h-5 w-5 text-primary" aria-hidden="true" />
            </div>
            <div>
              <h2 className="text-xl font-semibold">Create Link</h2>
              <p className="text-sm text-muted-foreground">Add a new link with analytics</p>
            </div>
          </div>
          <button
            onClick={closeCreateLinkModal}
            className="p-2 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Close create link modal"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={form.handleSubmit(onSubmit)} className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            {/* URL Input - Primary Focus */}
            <div className="space-y-2">
              <label htmlFor="url" className="block text-sm font-medium">
                Destination URL <span className="text-destructive">*</span>
              </label>
              <input
                type="url"
                id="url"
                autoComplete="url"
                inputMode="url"
                spellCheck={false}
                {...form.register('url')}
                className="w-full px-4 py-3 rounded-xl border bg-background focus:outline-none focus:ring-2 focus:ring-primary text-base"
                placeholder={placeholder.url}
                autoFocus={!isMobile}
              />
              {form.formState.errors.url && (
                <p className="text-sm text-destructive">{form.formState.errors.url.message}</p>
              )}
              {unsafeUrlError && (
                <div className="p-3 rounded-xl border border-orange-500/30 bg-orange-500/10">
                  <div className="flex items-start gap-2">
                    <ShieldAlert className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-orange-600">Unable to create link</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        This URL has been flagged as potentially unsafe.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Two Column Layout for Title and Custom Code */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Title */}
              <div className="space-y-2">
                <label htmlFor="title" className="block text-sm font-medium">
                  Title <span className="text-muted-foreground text-xs">(optional)</span>
                </label>
                <input
                  type="text"
                  id="title"
                  autoComplete="off"
                  spellCheck={true}
                  {...form.register('title')}
                  className="w-full px-4 py-2.5 rounded-xl border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder={placeholder.title}
                />
              </div>

              {/* Vanity URL (Custom Short Code) - Plus feature */}
              <VanityUrlField
                register={form.register}
                error={form.formState.errors.customCode?.message}
              />
            </div>

            {/* Project Selector - Only for Plus users */}
            {isPlusUser && projects.length > 0 && (
              <div className="space-y-2">
                <label htmlFor="projectId" className="flex items-center gap-2 text-sm font-medium">
                  <Layers className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                  Project
                </label>
                <select
                  id="projectId"
                  autoComplete="off"
                  {...form.register('projectId')}
                  className="w-full px-4 py-2.5 rounded-xl border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">No project</option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Tags */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium">
                <Tag className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                Tags <span className="text-muted-foreground text-xs">(optional)</span>
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  name="newTag"
                  autoComplete="off"
                  spellCheck={false}
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addTag();
                    }
                  }}
                  className="flex-1 px-4 py-2.5 rounded-xl border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="e.g., newsletter, facebook"
                />
                <button
                  type="button"
                  onClick={addTag}
                  className="px-4 py-2.5 rounded-xl border hover:bg-muted transition-colors"
                  aria-label="Add tag"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
              {tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary/10 text-primary text-sm"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeTag(tag)}
                        className="hover:text-primary/70"
                        aria-label={`Remove ${tag} tag`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Expiration & Limits - Compact Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label htmlFor="validUntil" className="flex items-center gap-2 text-sm font-medium">
                  <Clock className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                  Expires
                </label>
                <input
                  type="datetime-local"
                  id="validUntil"
                  autoComplete="off"
                  {...form.register('validUntil')}
                  className="w-full px-4 py-2.5 rounded-xl border bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="maxVisits" className="flex items-center gap-2 text-sm font-medium">
                  <Users className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                  Max Visits
                </label>
                <input
                  type="number"
                  id="maxVisits"
                  autoComplete="off"
                  inputMode="numeric"
                  {...form.register('maxVisits', { valueAsNumber: true })}
                  className="w-full px-4 py-2.5 rounded-xl border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Unlimited"
                  min={1}
                />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between gap-3 p-6 border-t bg-muted/30">
            <p className="text-xs text-muted-foreground hidden sm:block">
              Link will be active immediately after creation
            </p>
            <div className="flex gap-3 ml-auto">
              <button
                type="button"
                onClick={closeCreateLinkModal}
                className="px-4 py-2.5 rounded-xl border hover:bg-muted transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={createLinkMutation.isPending}
                className={cn(
                  'inline-flex items-center gap-2 px-6 py-2.5 rounded-xl font-semibold shadow transition-all',
                  'bg-primary text-primary-foreground hover:bg-primary/90',
                  'disabled:opacity-50 disabled:cursor-not-allowed'
                )}
              >
                {createLinkMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Creating…
                  </>
                ) : (
                  <>
                    <Link2 className="h-4 w-4" aria-hidden="true" />
                    Create Link
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
